/**
 * La **fiche d'une opération** — le panneau du dessin devenu un écran (`B-11`,
 * `EX-BO-004`, `EX-LDG-002`).
 *
 * ## Pourquoi un écran, et non le panneau du dessin
 *
 * Le porteur l'a demandé en regardant la page : « pour la page détail, je pense
 * que le mieux va être de créer une page détail à part, bien moderne, pour
 * mieux voir ». Il a raison, et la raison se compte : ce panneau doit porter
 * les écritures en partie double, le dénouement, la cause de l'échec, le lien
 * de réversal dans les deux sens, la version de plan qui a coté les frais,
 * l'origine, la clé d'idempotence **et** la commande de contre-écriture avec son
 * motif. Ce n'est plus un panneau — c'est un écran, et le forcer dans une
 * colonne de quatre cents points le rendrait illisible.
 *
 * ## La composition, et pourquoi celle-ci
 *
 * La première livraison posait **quatre panneaux de 306 points, tous de même
 * largeur et tous de même hauteur**, suivis de 172 points de vide : mesuré dans
 * le navigateur. Le montant — l'objet même de la fiche — était noyé dans une
 * phrase de sous-titre, pendant que la clé d'idempotence recevait autant de
 * place que les écritures. Une grille égale devant des contenus inégaux ne
 * compose pas : elle range.
 *
 * Le porteur a tranché la direction en envoyant une fiche qu'il trouvait juste,
 * et elle l'est : un **bandeau du montant** en tête, puis une colonne large qui
 * porte le travail et une colonne étroite qui porte le contexte. C'est repris
 * ici, avec deux écarts assumés :
 *
 *  - **le bandeau n'est pas rouge.** Sur le modèle il l'est par décoration ;
 *    ici le rouge est `status.danger` et signifie « échec ». Un bandeau rouge
 *    au-dessus d'une pastille verte dirait le contraire d'elle. Le bandeau
 *    prend donc l'encre de la marque, et **ne vire au rouge que sur une
 *    opération réellement en échec** — il porte alors une information au lieu
 *    d'un effet ;
 *  - **l'historique est une frise, et il est servi.** Chaque étape vient d'un
 *    champ que le serveur rend — `initiatedAt`, `completedAt`, `failureCode`,
 *    `ledgerPosted` — et aucune n'est déduite d'une autre. Une étape dont le
 *    champ est absent est **dite absente**, jamais devinée.
 *
 * ## C'est une SECONDE lecture, et elle est journalisée
 *
 * L'écran n'affiche pas la ligne déjà reçue : il appelle
 * `GET /api/backoffice/transactions/{id}`, qui inscrit au journal une entrée
 * **nommant cette opération**, le nombre d'écritures et le nombre d'identités
 * révélées. Dépiler la ligne aurait montré une fiche que le journal ne
 * mentionne pas — la promesse affichée et non tenue que le module Clients a
 * déjà refusée (`EX-BO-004`).
 *
 * ## Ce que la note du dessin promet, et qui est tenu
 *
 * « Un réversal ne modifie jamais une écriture : il en crée une inverse, sous
 * double validation (EX-LDG-002). » Les deux moitiés sont vraies et
 * vérifiables : la contre-écriture est une opération **nouvelle** qui désigne
 * celle qu'elle annule — `reversal_of_id`, protégée par un déclencheur et par un
 * index unique partiel —, et `MANUAL_REVERSAL` est en ✔✔ dans la matrice, pour
 * la seule finance. L'écran ne prononce donc jamais le réversal : il le
 * **demande**, et dit que la seconde signature manque encore.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import { FormShell } from '../shell/FormShell.js';
import type { FormFieldDescriptor } from '../shell/FormShell.js';
import { useApiClient } from '../http/api-context.js';
import { ApiError } from '../http/api-client.js';
import { fetchTransaction, requestManualReversal } from '../http/transactions.js';
import type { Transaction } from '../http/transactions.js';
import type { ActionAcknowledgement } from '../http/reconciliation.js';
import { PartyLink, StatusPill, transactionRoute, useTransactionFormats } from './transactions-display.js';

/** Le champ unique du motif de réversal. Le serveur l'exige, et l'inscrit au journal. */
const champsDuReversal: readonly FormFieldDescriptor[] = [
  { name: 'reason', labelKey: 'transactions.reversal.reason', required: true },
];

/**
 * Une étape de la frise, telle qu'un champ servi la rend.
 *
 * `tone` n'est pas une couleur : c'est l'un des trois états que le domaine
 * nomme, et le CSS décide de ce qu'il en peint. `at` est nul quand l'étape
 * n'a pas eu lieu — et une étape sans instant se rend **en attente**, jamais
 * avec l'instant de sa voisine.
 */
interface EtapeDeFrise {
  readonly cle: string;
  readonly libelle: string;
  readonly at: string | null;
  readonly detail: string | null;
  readonly tone: 'SUCCEEDED' | 'FAILED' | 'PENDING';
}

/** L'écran : une opération, ses écritures, son dénouement, sa cotation. */
export function TransactionDetailScreen(): ReactNode {
  const { t } = useTranslation();
  const client = useApiClient();
  const { transactionId } = useParams();
  const { montant, instant, libelleType, libelleEtat, libelleCanal, libelleInitiateur } =
    useTransactionFormats();

  const [operation, setOperation] = useState<Transaction | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [demande, setDemande] = useState<ActionAcknowledgement | null>(null);
  const [erreurDemande, setErreurDemande] = useState<string | null>(null);
  const [copie, setCopie] = useState(false);

  const lire = useCallback(async () => {
    if (transactionId === undefined) return;
    try {
      setOperation(await fetchTransaction(client, transactionId));
      setErreur(null);
    } catch (echec) {
      // La fiche lue est effacée avec l'échec : garder la précédente ferait
      // lire une opération sous la référence d'une autre.
      setOperation(null);
      setErreur(echec instanceof ApiError ? echec.messageKey : 'errors.server');
    }
  }, [client, transactionId]);

  useEffect(() => {
    void lire();
  }, [lire]);

  /**
   * **Demande** une contre-écriture. Elle ne la prononce pas.
   *
   * Le serveur met la demande en attente et rend l'identifiant de la seconde
   * signature. L'écran ne dit jamais « l'opération est contre-passée » : tant
   * qu'un identifiant de demande revient, le réversal attend un second
   * signataire, et l'annoncer fait serait annoncer de l'argent rendu qui ne
   * l'est pas.
   */
  const demanderLeReversal = useCallback(
    async (motif: string) => {
      if (operation === null) return;
      setDemande(null);
      try {
        setDemande(await requestManualReversal(client, operation.id, motif));
        setErreurDemande(null);
        // L'opération est relue : la demande ne contre-passe rien, mais c'est
        // le serveur qui doit le dire, pas cet écran.
        await lire();
      } catch (echec) {
        setErreurDemande(echec instanceof ApiError ? echec.messageKey : 'errors.server');
      }
    },
    [client, lire, operation],
  );

  if (erreur !== null) {
    return (
      <section className="transactions" aria-label={t('transactions.detail.landmark')}>
        <p className="transactions__notice" role="alert">
          {t(erreur)}
        </p>
        <Link className="transactions__back" to="/transactions">
          {t('transactions.detail.back')}
        </Link>
      </section>
    );
  }

  if (operation === null) {
    return (
      <section className="transactions" aria-label={t('transactions.detail.landmark')}>
        <p className="transactions__idle">{t('transactions.detail.loading')}</p>
      </section>
    );
  }

  /**
   * Le **solde des écritures** : la somme algébrique des débits et des crédits.
   *
   * C'est la ligne « Solde des écritures — 0 F » du dessin, et **la seule chose
   * de ce produit qu'un contrôleur peut vérifier lui-même**. Elle est calculée à
   * partir des écritures servies, et c'est la seule exception à la règle « aucun
   * calcul côté front » — pour une raison qui est justement celle de la règle :
   * un solde servi par le même système que les écritures ne prouverait rien de
   * plus que les écritures. Le calculer ici, c'est le recalculer *contre* le
   * serveur.
   */
  const solde = operation.entries.reduce(
    (cumul, ecriture) => cumul + (ecriture.direction === 'D' ? ecriture.amount : -ecriture.amount),
    0,
  );
  const attendu = operation.amount + operation.feeAmount;

  /**
   * La frise, **du plus récent au plus ancien**.
   *
   * L'issue est le fait que l'on vient chercher : elle se lit en premier, là où
   * l'œil tombe. Chaque étape naît d'un champ servi, et une étape qui n'a pas
   * eu lieu porte le ton `PENDING` avec son instant à nul — elle ne réutilise
   * jamais l'instant de sa voisine pour faire bonne figure.
   */
  const frise: readonly EtapeDeFrise[] = [
    {
      cle: 'posted',
      libelle: operation.ledgerPosted
        ? t('transactions.detail.stepPosted')
        : t('transactions.detail.stepNotPosted'),
      at: null,
      detail: t(
        operation.ledgerPosted
          ? 'transactions.detail.postedYes'
          : 'transactions.detail.postedNo',
      ),
      tone: operation.ledgerPosted ? 'SUCCEEDED' : 'PENDING',
    },
    {
      cle: 'settled',
      libelle:
        operation.failureCode !== null
          ? t('transactions.detail.stepFailed')
          : operation.completedAt === null
            ? t('transactions.detail.stepPending')
            : t('transactions.detail.stepSettled'),
      at: operation.completedAt,
      detail: operation.failureCode ?? operation.narrative,
      tone:
        operation.failureCode !== null
          ? 'FAILED'
          : operation.completedAt === null
            ? 'PENDING'
            : 'SUCCEEDED',
    },
    {
      cle: 'initiated',
      libelle: t('transactions.detail.stepInitiated'),
      at: operation.initiatedAt,
      detail: t('transactions.detail.stepInitiatedBy', {
        channel: libelleCanal(operation.channel),
        initiator: libelleInitiateur(operation.initiatedByType),
      }),
      tone: 'SUCCEEDED',
    },
  ];

  const copierLaReference = () => {
    // Une promesse d'action qui ne tient pas est pire que l'action absente :
    // l'issue est DITE, et l'échec ne se confond pas avec le succès.
    void navigator.clipboard
      ?.writeText(operation.publicRef)
      .then(() => {
        setCopie(true);
      })
      .catch(() => {
        setCopie(false);
      });
  };

  return (
    <section className="transactions transactions--detail" aria-label={t('transactions.detail.landmark')}>
      <header className="transactions__head">
        <Link className="transactions__back" to="/transactions">
          {t('transactions.detail.back')}
        </Link>
        <div className="transactions__head-row">
          <div className="transactions__head-title">
            <h2 className="transactions__title" data-testid="transaction-reference">
              {operation.publicRef}
            </h2>
            <StatusPill status={operation.status} tone={operation.statusTone} />
            <span className="transactions__type-pill">{libelleType(operation.txType)}</span>
          </div>
          <div className="transactions__head-actions">
            <button
              className="transactions__ghost-button"
              type="button"
              onClick={copierLaReference}
            >
              {t('transactions.detail.copyRef')}
            </button>
          </div>
        </div>
        <p className="transactions__head-meta">
          {t('transactions.detail.headline', {
            type: libelleType(operation.txType),
            amount: montant(operation.amount, operation.currency),
            at: instant(operation.initiatedAt),
          })}
        </p>
        {copie ? (
          <p className="transactions__result" role="status">
            {t('transactions.detail.copied')}
          </p>
        ) : null}
      </header>

      {/* **Le bandeau du montant.** L'objet de la fiche, à la taille de son
          importance. Son ton vient de l'état servi : encre par défaut, rouge
          sur un échec réel — jamais rouge par décoration. */}
      <div
        className="transactions__hero"
        data-tone={operation.statusTone}
        data-testid="transaction-hero"
      >
        <div className="transactions__hero-figure">
          <span className="transactions__hero-label">{t('transactions.detail.amountLabel')}</span>
          <strong className="transactions__hero-amount">
            {montant(operation.amount, operation.currency)}
          </strong>
        </div>
        <div className="transactions__hero-figure transactions__hero-figure--end">
          <span className="transactions__hero-label">{t('transactions.detail.feeLabel')}</span>
          <strong className="transactions__hero-fee">
            {montant(operation.feeAmount, operation.currency)}
          </strong>
        </div>
      </div>

      <div className="transactions__layout">
        <div className="transactions__main">
          {/* --- Ce que l'opération a coûté, et la confrontation d'EX-TAR-003 --- */}
          <section className="transactions__panel">
            <h3 className="transactions__panel-title">
              {t('transactions.detail.financialTitle')}
            </h3>
            <dl className="transactions__figures">
              <div className="transactions__figure">
                <dt>{t('transactions.detail.grossLabel')}</dt>
                <dd>{montant(operation.amount, operation.currency)}</dd>
              </div>
              <div className="transactions__figure">
                <dt>{t('transactions.detail.feeLabel')}</dt>
                <dd>{montant(operation.feeAmount, operation.currency)}</dd>
              </div>
              {/* La confrontation que le dessin ne fait pas : `total_debited`
                  contre `amount + fee_amount`. Un écart entre les deux est un
                  prélèvement qui ne correspond pas à ce qui a été annoncé
                  (EX-TAR-003), et il se peint alors comme un défaut. */}
              <div
                className="transactions__figure transactions__figure--total"
                data-matches={operation.totalDebited === attendu}
              >
                <dt>{t('transactions.detail.totalDebited')}</dt>
                <dd data-testid="transaction-total-debited">
                  {operation.totalDebited === attendu
                    ? t('transactions.detail.totalMatches', {
                        total: montant(operation.totalDebited, operation.currency),
                      })
                    : t('transactions.detail.totalDiffers', {
                        total: montant(operation.totalDebited, operation.currency),
                        expected: montant(attendu, operation.currency),
                      })}
                </dd>
              </div>
            </dl>
          </section>

          {/* **Les deux acteurs, et les boutons qui mènent à leur fiche.** C'est
              ce que le porteur a demandé : « des boutons pour aller sur une page
              détail de ces acteurs ». Ils vivent ICI et non dans la table : la
              colonne des contreparties en a été retirée, et une rangée qui
              s'ouvre déjà au clic ne peut pas porter un second élément
              interactif. */}
          <section className="transactions__panel">
            <h3 className="transactions__panel-title">
              {t('transactions.detail.partiesTitle')}
            </h3>
            <div className="transactions__parties">
              <div className="transactions__party-card">
                <span className="transactions__party-role">
                  {t('transactions.detail.debitSide')}
                </span>
                <PartyLink party={operation.debit} />
              </div>
              <div className="transactions__party-card">
                <span className="transactions__party-role">
                  {t('transactions.detail.creditSide')}
                </span>
                <PartyLink party={operation.credit} />
              </div>
            </div>
          </section>

          {/* --- Écritures — partie double, le premier panneau du dessin ---
              Rendues en TABLE, et sur toute la largeur de la colonne de travail.
              La première livraison les serrait dans 272 points pour 409 de
              contenu : le solde après écriture — le champ qui PROUVE l'écriture
              — était peint hors de sa carte, sous la carte voisine, sans même
              une barre de défilement pour laisser deviner qu'il existait. */}
          <section className="transactions__panel">
            <h3 className="transactions__panel-title">
              {t('transactions.detail.entriesTitle')}
            </h3>
            {operation.entries.length === 0 ? (
              <p className="transactions__idle" data-testid="transaction-no-entries">
                {t('transactions.detail.entriesEmpty')}
              </p>
            ) : (
              <>
                <table className="transactions__entries" data-testid="transaction-entries">
                  <thead>
                    <tr>
                      <th scope="col">{t('transactions.detail.entryDirection')}</th>
                      <th scope="col">{t('transactions.detail.entryAccount')}</th>
                      <th scope="col">{t('transactions.detail.entryType')}</th>
                      <th scope="col">{t('transactions.detail.entryAmount')}</th>
                      <th scope="col">{t('transactions.detail.entryBalance')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operation.entries.map((ecriture, rang) => (
                      <tr
                        className="transactions__entry"
                        data-direction={ecriture.direction}
                        key={`${ecriture.accountNumber}-${ecriture.entryType}-${String(rang)}`}
                      >
                        <td className="transactions__entry-side">
                          {t(
                            ecriture.direction === 'D'
                              ? 'transactions.detail.debit'
                              : 'transactions.detail.credit',
                          )}
                        </td>
                        <td className="transactions__entry-account">{ecriture.accountNumber}</td>
                        <td className="transactions__entry-type">{ecriture.entryType}</td>
                        <td className="transactions__entry-amount">
                          {montant(ecriture.amount, ecriture.currency)}
                        </td>
                        <td className="transactions__entry-balance">
                          {/* Un compte de cantonnement ne tient pas de solde :
                              nul veut dire « ce compte n'en tient pas », et
                              « 0 F » se lirait comme un compte vidé. */}
                          {ecriture.balanceAfter === null
                            ? t('transactions.detail.noBalance')
                            : montant(ecriture.balanceAfter, ecriture.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p
                  className="transactions__balance"
                  data-balanced={solde === 0}
                  data-testid="transaction-balance"
                >
                  {t('transactions.detail.balance', {
                    amount: montant(solde, operation.currency),
                  })}
                </p>
              </>
            )}
          </section>

          {/* --- La commande du dessin qui est SERVIE, avec le motif qu'elle exige --- */}
          <section className="transactions__panel">
            <h3 className="transactions__panel-title">{t('transactions.reversal.title')}</h3>
            {operation.reversedByRef !== null ? (
              // Une opération déjà contre-passée ne se contre-passe pas deux
              // fois, et le bouton n'est pas grisé : il n'est pas là. L'index
              // unique partiel de `reversal_of_id` le garantit côté base ;
              // l'écran le DIT plutôt que de laisser cliquer sur un refus
              // certain.
              <p className="transactions__idle" data-testid="transaction-already-reversed">
                {t('transactions.reversal.alreadyReversed', {
                  reference: operation.reversedByRef,
                })}
              </p>
            ) : (
              <FormShell
                fields={champsDuReversal}
                onSubmit={(valeurs) => demanderLeReversal(valeurs.reason.trim())}
                submitLabelKey="transactions.reversal.submit"
              />
            )}

            {erreurDemande === null ? null : (
              <p className="transactions__notice" role="alert">
                {t(erreurDemande)}
              </p>
            )}

            {demande === null ? null : (
              <div
                className="transactions__acknowledgement"
                data-testid="transaction-reversal-acknowledgement"
              >
                {demande.approvalRequestId === null ? (
                  <p className="transactions__result">
                    {t('transactions.reversal.outcome', { outcome: demande.outcome })}
                  </p>
                ) : (
                  <p className="transactions__result" data-testid="transaction-reversal-pending">
                    {t('transactions.reversal.pending', {
                      reference: demande.approvalRequestId,
                    })}
                  </p>
                )}
                {demande.detail === null ? null : (
                  <p className="transactions__result">{demande.detail}</p>
                )}
              </div>
            )}

            {/* **La note du dessin, à l'endroit où le dessin la met** — sous les
                commandes, en petit. Et elle est VRAIE : le réversal n'est pas une
                écriture modifiée, c'est une opération nouvelle qui porte
                `reversal_of_id`, et la matrice ne l'accorde qu'en double
                validation. */}
            <p className="transactions__note">{t('transactions.note')}</p>
          </section>
        </div>

        <aside className="transactions__side">
          {/* --- Les instants, séparés des faits métier --- */}
          <section className="transactions__panel">
            <h3 className="transactions__panel-title">{t('transactions.detail.datesTitle')}</h3>
            <dl className="transactions__facts">
              <dt>{t('transactions.detail.initiatedAt')}</dt>
              <dd>{instant(operation.initiatedAt)}</dd>
              <dt>{t('transactions.detail.completedAt')}</dt>
              <dd data-testid="transaction-completed">
                {operation.completedAt === null
                  ? t('transactions.detail.notSettled')
                  : instant(operation.completedAt)}
              </dd>
              <dt>{t('transactions.detail.status')}</dt>
              {/* L'énumération du serveur ne s'affiche PAS telle quelle : la
                  fiche écrivait `REUSSIE`, en capitales et sans accent, à deux
                  cents points d'une pastille qui écrit « Réussie ». */}
              <dd data-testid="transaction-status">{libelleEtat(operation.status)}</dd>
            </dl>
          </section>

          {/* --- L'historique : ce qui est arrivé à cet argent --- */}
          <section className="transactions__panel">
            <h3 className="transactions__panel-title">
              {t('transactions.detail.timelineTitle')}
            </h3>
            <ol className="transactions__timeline" data-testid="transaction-timeline">
              {frise.map((etape) => (
                <li className="transactions__moment" data-tone={etape.tone} key={etape.cle}>
                  <span className="transactions__moment-mark" aria-hidden="true" />
                  <div className="transactions__moment-body">
                    <span className="transactions__moment-label">{etape.libelle}</span>
                    {etape.at === null ? null : (
                      <span className="transactions__moment-at">{instant(etape.at)}</span>
                    )}
                    {etape.detail === null ? null : (
                      <span className="transactions__moment-detail">{etape.detail}</span>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* --- Ce que la base porte et que le dessin ignore --- */}
          <section className="transactions__panel">
            <h3 className="transactions__panel-title">
              {t('transactions.detail.identifiersTitle')}
            </h3>
            <dl className="transactions__facts">
              <dt>{t('transactions.detail.planVersion')}</dt>
              {/* « Pourquoi ces frais-là » : la version de plan qui a coté
                  l'opération. Son absence est une information — un réversal
                  n'est pas tarifé —, jamais une lacune à combler par la version
                  du jour. */}
              <dd data-testid="transaction-plan">
                {operation.planCode === null
                  ? t('transactions.detail.notPriced')
                  : t('transactions.detail.planValue', {
                      plan: operation.planCode,
                      version: operation.planVersionNo ?? 0,
                      status: operation.planStatus ?? '',
                    })}
              </dd>
              <dt>{t('transactions.detail.channel')}</dt>
              <dd data-testid="transaction-channel">
                {t('transactions.detail.channelValue', {
                  channel: libelleCanal(operation.channel),
                  initiator: libelleInitiateur(operation.initiatedByType),
                })}
              </dd>
              <dt>{t('transactions.detail.idempotencyKey')}</dt>
              {/* Deux appels servis par la même clé sont LE MÊME paiement. La
                  clé est rendue entière : c'est elle qu'un contrôleur recopie
                  dans la barre de recherche pour voir si une seconde opération
                  la porte. */}
              <dd className="transactions__key" data-testid="transaction-idempotency">
                {operation.idempotencyKey}
              </dd>
              <dt>{t('transactions.detail.reverses')}</dt>
              <dd data-testid="transaction-reverses">
                {operation.reversesId === null || operation.reversesRef === null ? (
                  t('transactions.detail.reversesNothing')
                ) : (
                  <Link to={transactionRoute(operation.reversesId)}>{operation.reversesRef}</Link>
                )}
              </dd>
              <dt>{t('transactions.detail.reversedBy')}</dt>
              <dd data-testid="transaction-reversed-by">
                {operation.reversedById === null || operation.reversedByRef === null ? (
                  t('transactions.detail.notReversed')
                ) : (
                  <Link to={transactionRoute(operation.reversedById)}>
                    {operation.reversedByRef}
                  </Link>
                )}
              </dd>
            </dl>
          </section>
        </aside>
      </div>
    </section>
  );
}
